<script setup>
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import SzStatusSegmented from './widgets/conversation/solarz/SzStatusSegmented.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  appliedFiltersCount: { type: Number, default: 0 },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const onSzStatusChange = value => {
  emit('basicFilterChange', value, 'status');
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const filterBadgeCount = computed(() => {
  if (!props.hasAppliedFilters) return 0;
  return props.appliedFiltersCount || 0;
});

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div class="sz-chl-header">
    <!-- SZ: segmented status (Abertas/Fechadas/Todas) — sempre visível -->
    <SzStatusSegmented
      :active-status="activeStatus"
      class="sz-chl-status"
      @change-status="onSzStatusChange"
    />

    <div class="sz-chl-actions">
      <!--
        SZ: X+Filtro AGRUPADOS em pill conectado quando há filtros aplicados (Figma).
        Sem filtros → só o Filter standalone (rounded full).
      -->
      <div
        class="sz-chl-filtergroup"
        :class="{ 'sz-chl-filtergroup--joined': hasAppliedFiltersOrActiveFolders }"
      >
        <!-- X: limpa filtros / sai da pasta. Só aparece quando há filtros. -->
        <div
          v-if="hasAppliedFiltersOrActiveFolders"
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          class="sz-chl-iconbtn sz-chl-iconbtn--x"
          role="button"
          tabindex="0"
          aria-label="Limpar filtros"
          @click="emit('resetFilters')"
          @keydown.enter.prevent="emit('resetFilters')"
          @keydown.space.prevent="emit('resetFilters')"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </div>

        <!-- Filtro: sempre visível, com badge quando há filtros -->
        <div
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          class="sz-chl-iconbtn sz-chl-iconbtn--filter"
          role="button"
          tabindex="0"
          :aria-label="$t('FILTER.TOOLTIP_LABEL')"
          @click="emit('filtersModal')"
          @keydown.enter.prevent="emit('filtersModal')"
          @keydown.space.prevent="emit('filtersModal')"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.625 10.625C15.625 10.7908 15.5592 10.9497 15.4419 11.0669C15.3247 11.1842 15.1658 11.25 15 11.25H5C4.83424 11.25 4.67527 11.1842 4.55806 11.0669C4.44085 10.9497 4.375 10.7908 4.375 10.625C4.375 10.4592 4.44085 10.3003 4.55806 10.1831C4.67527 10.0658 4.83424 10 5 10H15C15.1658 10 15.3247 10.0658 15.4419 10.1831C15.5592 10.3003 15.625 10.4592 15.625 10.625ZM18.125 6.25H1.875C1.70924 6.25 1.55027 6.31585 1.43306 6.43306C1.31585 6.55027 1.25 6.70924 1.25 6.875C1.25 7.04076 1.31585 7.19973 1.43306 7.31694C1.55027 7.43415 1.70924 7.5 1.875 7.5H18.125C18.2908 7.5 18.4497 7.43415 18.5669 7.31694C18.6842 7.19973 18.75 7.04076 18.75 6.875C18.75 6.70924 18.6842 6.55027 18.5669 6.43306C18.4497 6.31585 18.2908 6.25 18.125 6.25ZM11.875 13.75H8.125C7.95924 13.75 7.80027 13.8158 7.68306 13.9331C7.56585 14.0503 7.5 14.2092 7.5 14.375C7.5 14.5408 7.56585 14.6997 7.68306 14.8169C7.80027 14.9342 7.95924 15 8.125 15H11.875C12.0408 15 12.1997 14.9342 12.3169 14.8169C12.4342 14.6997 12.5 14.5408 12.5 14.375C12.5 14.2092 12.4342 14.0503 12.3169 13.9331C12.1997 13.8158 12.0408 13.75 11.875 13.75Z" fill="currentColor"/>
          </svg>
          <!-- Badge verde com count (posicionado em relação ao botão) -->
          <span v-if="filterBadgeCount > 0" class="sz-chl-badge">{{ filterBadgeCount }}</span>
        </div>
        <!-- Teleport target pra modal nativo de filtro (compat com folder edit) -->
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
        <!-- Teleport target pra modal de salvar pasta (mantém compat) -->
        <div
          id="saveFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>

      <!-- SZ: Sort toggle (Mais recentes ↔ Mais antigas) — sempre visível -->
      <ConversationBasicFilter
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <div class="sz-chl-switchlayout">
        <SwitchLayout
          :is-on-expanded-layout="isOnExpandedLayout"
          @toggle="toggleConversationLayout"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.sz-chl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
  min-height: 55px;
  background: #FFFFFF;
  box-sizing: border-box;
  /* Container query: filhos reagem à largura do header, não da viewport */
  container-type: inline-size;
  container-name: szchlh;
}
.dark .sz-chl-header {
  border-bottom-color: #303138;
  background: #17181B;
}
.sz-chl-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 1;
  min-width: 0;
}

.sz-chl-switchlayout {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* Em containers apertados, o switch-layout (último item à direita) vaza pra
   fora. Esconde — é redundante quando o espaço é crítico.
   Threshold 380px cobre w-[340px] com folga de 40px e w-[412px] no limite. */
@container szchlh (max-width: 380px) {
  .sz-chl-actions { gap: 6px; }
  .sz-chl-switchlayout { display: none !important; }
}

/* Grupo X+Filtro — quando hasAppliedFilters, viram pill conectado */
.sz-chl-filtergroup {
  position: relative;
  display: inline-flex;
  align-items: center;
}
/* Sem filtros: filter standalone com bordas full */
.sz-chl-filtergroup .sz-chl-iconbtn--filter {
  border-radius: 8px;
}
/* Com filtros: X esquerda + Filter direita = pill conectado */
.sz-chl-filtergroup--joined .sz-chl-iconbtn--x {
  border-radius: 8px 0 0 8px;
  border-right: none;
}
.sz-chl-filtergroup--joined .sz-chl-iconbtn--filter {
  border-radius: 0 8px 8px 0;
}

/* Botão quadrado 32x32 (X, Filter) — Figma */
.sz-chl-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1.5px solid #E5E5E5;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  color: #3F3F3F;
  transition: background 0.15s, border-color 0.15s;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
}
.sz-chl-iconbtn:hover { background: rgba(0, 0, 0, 0.03); }
.sz-chl-iconbtn:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
  z-index: 1;
}

/* Badge verde sobre o ícone de filtro */
.sz-chl-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: #59AA59;
  color: #17181B;
  border-radius: 999px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  z-index: 2;
  pointer-events: none;
}

/* Dark mode (Figma) */
.dark .sz-chl-iconbtn {
  border-color: #303138;
  color: #FFFFFF;
  background: #1D1E21;
}
.dark .sz-chl-iconbtn:hover { background: #26282B; }
.dark .sz-chl-badge {
  background: #59AA59;
  color: #17181B;
}
</style>
